import * as React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Helmet from 'react-helmet';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import tw from 'tailwind.macro';
import 'typeface-montserrat';

import Content from '../components/Content';
import Description from '../components/Description';
import Header from '../components/Header';
import Social from '../components/Social';
import Borders from '../components/Borders';

import favicon from '../favicon.png';
import rightArrow from '../right-arrow.svg';
import github from '../github.svg';
import damascusBg from '../damascus.svg';

const GlobalStyles = createGlobalStyle`
  *::before,
  *::after {
    box-sizing: border-box;
  }
  ::selection {
    color: white;
    background-color: #f6993f;
  }
  html {
    background: #f8c9a8;
    border: 0;
    margin: 0;
    font-size: 18px;
  }
  body {
    border: 0;
    margin: 0;
    padding: 0;
  }
  select {
    appearance: none;
    border:none;
    font-size: 1rem;
    width: 100%;
    color: white;
    padding: .75rem 1rem;
    border-radius: .25rem;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' fill='white'%3E%3Cpolygon points='0,0 100,0 50,50'/%3E%3C/svg%3E") #e0b794 no-repeat 98% 77%;
    background-size: 25px;
    box-shadow: 0 4px 8px 0 rgba(0,0,0,.12), 0 2px 4px 0 rgba(0,0,0,.08);
    &:focus {
      outline: 0;
      box-shadow: 0 0 0 3px rgba(101,116,205,.5);
    }
    &:hover {
      cursor: pointer;
    }
  }
  select::-ms-expand {
    display:none;
  }
  select option {
    background: #2f2f2f;
    font-size: 1rem;
  }
`;

const Page = styled.div`
  ${tw`text-white font-sans p-0 m-0 bg-indigo-darker antialiased border-8 sm:border-16 border-solid leading-normal relative`};
  border-color: #3f3f3f;
  background: #2f2f2f
    url("${damascusBg}");
  min-height: calc(100vh - 16px);
  @media (min-width: 576px) {
    min-height: calc(100vh - 32px);
  }
  span {
    ${tw`text-orange`};
  }
  i {
    ${tw`text-orange-light`};
  }
`;

const SliderWrapper = styled.section`
  ${tw`sm:px-8 px-4 md:px-24`};
`;

const Footer = styled.footer`
  ${tw`text-center pb-8 pt-16 text-xs text-grey-light tracking-wide z-50 uppercase`};
  a {
    ${tw`text-orange hover:text-orange-light no-underline`};
  }
`;

const Item = styled.div`
  ${tw`bg-black rounded-lg shadow-lg flex`};
  height: 525px;
  @media (max-width: 500px) {
    height: 450px;
  }
`;

const ItemContent = styled.div`
  ${tw`py-8 px-6 flex flex-wrap content-between relative`};
`;

const Top = styled.div`
  ${tw`z-30 flex flex-col`};
`;

const Bottom = styled.div`
  ${tw`z-30`};
`;

const Repo = styled("a")`
  ${tw`text-white text-sm inline-block mb-4 py-1 tracking-wide no-underline opacity-75`};
  transition: all 0.4s ease-in-out;
  img {
    width: 16px;
    height: 16px;
    margin-right: 10px;
    position: relative;
    top: 2px;
    transition: transform 0.3s ease-in-out;
  }
  &:hover {
    ${tw`opacity-100`};
  }
`;

const Desc = styled.div`
  ${tw`text-sm text-white opacity-75`};
`;

const BGImage = styled.div`
  ${tw`absolute pin rounded-lg`};
  .gatsby-image-outer-wrapper {
    position: static !important;
  }
  .gatsby-image-wrapper {
    position: static !important;
  }
  img {
    ${tw`rounded-lg`};
    opacity: 0.5 !important;
  }
`;

const ItemTitle = styled.h2`
  ${tw`text-white text-3xl mb-4`};
`;

const Gradient = styled.div`
  ${tw`absolute pin rounded-lg z-20`};
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0) 40%,
    rgba(0, 0, 0, 0.75) 100%
  );
`;

const Divider = styled.div`
  ${tw`bg-orange w-16 mb-4`};
  height: 3px;
`;

const FeaturesWrapper = styled.div`
  ${tw`text-grey-lighter text-xs flex items-end`};
  min-height: 40px;
`;

const YearWrapper = styled.div`
  ${tw`text-grey-lighter text-xs font-bold flex items-end`};
  min-height: 25px;
`;

const Heading = styled.h2`
  ${tw`text-2xl md:text-4xl font-normal`};
`;

const Grid = styled.div`
  ${tw`py-12`};
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  grid-gap: 30px;
`;

const Timeline = styled.ul`
  &:before {
    background-color: white;
    content: '';
    position: absolute;
    top: 0;
    left: 2em;
    width: 2px;
    height: 100%;

    @media (max-width: 600px) {
      margin-left: -30px;
    }
  }
  list-style-type: none;
  margin: 4em 0;
  position: relative;
  max-width: 46em;
`;

const TimelineEventIcon = styled.label`
  -moz-transition: -moz-transform 0.2s ease-in;
  -o-transition: -o-transform 0.2s ease-in;
  -webkit-transition: -webkit-transform 0.2s ease-in;
  transition: transform 0.2s ease-in;
  -moz-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  -webkit-transform: rotate(45deg);
  transform: rotate(45deg);
  background-color: white;
  outline: 10px solid transparent;
  display: block;
  margin: 0.5em 0.5em 0.5em -0.65em;
  position: absolute;
  top: 0;
  width: 1em;
  height: 1em;
`;

const TimelineEventDuration = styled.ul`
  -moz-transition: box-shadow 0.5s ease-in 0.1s;
  -o-transition: box-shadow 0.5s ease-in 0.1s;
  -webkit-transition: box-shadow 0.5s ease-in;
  -webkit-transition-delay: 0.1s;
  transition: box-shadow 0.5s ease-in 0.1s;
  color: white;
  font-size: 0.75em;
  background-color: black;
  -moz-box-shadow: inset 0 0 0 0em #ef795a;
  -webkit-box-shadow: inset 0 0 0 0em #ef795a;
  box-shadow: inset 0 0 0 0em #ef795a;
  display: inline-block;
  margin-bottom: 1.2em;
  padding: 0.25em 1em 0.2em 1em;
`;

const TimelineEventCopy = styled.div`
  h3 {
    font-size: 1.75em;
  }
  h4 {
    font-size: 1.2em;
    margin-bottom: 1.2em;
  }
  strong {
    font-weight: 700;
  }
  p:not(${TimelineEventDuration}) {
    padding-bottom: 1.2em;
  }
  padding-top: 2em;
  position: relative;
  top: -1.875em;
  left: 4em;
  width: 80%;
`;

const TimelineEvent = styled.li`
  &:hover ${TimelineEventIcon} {
    -moz-transform: rotate(-45deg);
    -ms-transform: rotate(-45deg);
    -webkit-transform: rotate(-45deg);
    transform: rotate(-45deg);
    background-color: #a83279;
  }
  &:hover ${TimelineEventDuration} {
    -moz-box-shadow: inset 40em 0 0 0 #a83279;
    -webkit-box-shadow: inset 40em 0 0 0 #a83279;
    box-shadow: inset 40em 0 0 0 #a83279;
  }
  position: relative;
  @media (max-width: 600px) {
    margin-left: -30px;
  }
`;

let Preview;

type PageProps = {
  data: {
    allSitesYaml: {
      edges: {
        node: {
          id: string;
          title: string;
          url: string;
          name: string;
          description: string;
          preview: string;
          features: string[];
          year: string;
          cover: {
            childImageSharp: {
              fluid: {
                aspectRatio: number;
                src: string;
                srcSet: string;
                sizes: string;
                base64: string;
                tracedSVG: string;
                srcWebp: string;
                srcSetWebp: string;
              };
            };
          };
        };
      }[];
    };
    site: {
      siteMetadata: {
        siteTitle: string;
        siteDescription: string;
        siteUrl: string;
      };
    };
  };
};

const Index: React.FunctionComponent<PageProps> = ({
  data: {
    allSitesYaml: { edges: sites },
    site: { siteMetadata }
  }
}) => {
  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>{siteMetadata.siteTitle}</title>
        <meta name="description" content={siteMetadata.siteDescription} />
        <meta name="image" content={favicon} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="" />
        <meta property="og:url" content={siteMetadata.siteUrl} />
        <meta property="og:title" content={siteMetadata.siteTitle} />
        <meta
          property="og:description"
          content={siteMetadata.siteDescription}
        />
        <meta property="og:image" content={favicon} />
      </Helmet>
      <Page>
        <Borders />
        <Header />
        <Content>
          <Heading>
            A little about me<span>,</span>
          </Heading>
          <Description>
            <p>
              <i>Software Engineer, creator and human, from India.</i>
            </p>
            <p>
              I've completed my Bachelor’s in Computer Sciences
              <span>,</span> but everything I know of and specialize in
              <span>,</span> has been self-taught<span>.</span> I’ve always
              enjoyed exploring multiple subjects and learning new stuff for self growth or out of sheer curiosity<span>.</span> I’m happy my
              career is one where a primary requirement for staying relevant is
              to keep learning<span>.</span>
              <br />
              <br />
              Being an active member of the Hacker community<span>,</span> I've built many prize winning projects<span>,</span> judged at hackathons and have been a speaker at high profile conferences like <a target="_blank" href="https://fosdem.org">FOSDEM</a><span>,</span> <a target="_blank" href="https://hackcon.mlh.io/">Hackcon</a> etc<span>.</span>
              <br />
              <br />
              Apart from work<span>,</span> I contribute to improve Open Source software<span>,</span> maintain some of my <a target="_blank" href="https://github.com/kartikcho">side projects</a> or explore new hobbies<span>!</span>
              {/* I've helped improve{' '}
              <a href="https://developer.mozilla.org/en-US/docs/Tools">
                Firefox Devtools
              </a>
              <span>,</span>{' '}
              <a href="https://github.com/facebook/react">React Devtools</a>
              <span>,</span> and many such amazing projects out there
              <span>!</span> */}
            </p>
            <hr></hr>
            <br></br>
          </Description>
        </Content>

        <SliderWrapper>
          <Heading>
            Meet some of my Open Source projects<span>!</span>
          </Heading>
          <Grid>
            {sites.map(site => {
              const {
                id,
                title,
                description,
                preview,
                features,
                year,
                cover,
                url
              } = site.node;
              console.log(site.node.year);
              if (preview === '#') {
                Preview = styled("a")`
                  display: none;
                `;
              } else {
                Preview = styled("a")`
                  ${tw`text-white inline-block text-xl relative mb-0 py-1 tracking-wide no-underline uppercase`};
                  img {
                    width: 18px;
                    height: 18px;
                    margin-left: 10px;
                    position: relative;
                    top: 1px;
                    transition: transform 0.3s ease-in-out;
                  }
                  &:hover {
                    img {
                      transform: translateX(10px);
                    }
                  }
                `;
              }
              return (
                <Item key={id}>
                  <ItemContent>
                    <Top id="top">
                      <Preview target="_blank" href={preview}>
                        Preview{' '}
                        <img src={rightArrow} alt="Arrow" aria-hidden="true" />
                      </Preview>
                      <Repo target="_blank" href={url}>
                        <img src={github} alt="Arrow" aria-hidden="true" />{' '}
                        GitHub
                      </Repo>
                      <Desc>{description}</Desc>
                    </Top>
                    <Bottom>
                      <ItemTitle>{title}</ItemTitle>
                      <Divider />
                      <FeaturesWrapper>{features.join(', ')}</FeaturesWrapper>
                      <YearWrapper>{year}</YearWrapper>
                    </Bottom>
                    <BGImage>
                      <Gradient />
                      <Img fluid={cover.childImageSharp.fluid} />
                    </BGImage>
                  </ItemContent>
                </Item>
              );
            })}
          </Grid>
        </SliderWrapper>
        <Content>
          <Heading>My past work experience</Heading>
          <Timeline>
            <TimelineEvent>
              <TimelineEventIcon></TimelineEventIcon>
              <TimelineEventCopy>
                <TimelineEventDuration>
                  Nov 2021 - Present
                </TimelineEventDuration>
                <h3>Senior Software Engineer</h3>
                <h4>Windsor.io</h4>
                <p>
                Built multiple products and systems from scratch using NextJS, GraphQL (using Relay), Postgres and MUI + styled components.
                </p>
              </TimelineEventCopy>
            </TimelineEvent>
            <TimelineEvent>
              <TimelineEventIcon></TimelineEventIcon>
              <TimelineEventCopy>
                <TimelineEventDuration>
                  May 2021 - Nov 2021
                </TimelineEventDuration>
                <h3>Frontend Engineer</h3>
                <h4>Klinify</h4>
                <p>
                Built new features, components and rewrote existing code using React, Redux and CSS-in-JS.
                <br />
                <br />
                Surveyed and executed a tech debt cleanup project, addressing 169 tickets in a 2-month timeframe.
                </p>
              </TimelineEventCopy>
            </TimelineEvent>
            <TimelineEvent>
              <TimelineEventIcon></TimelineEventIcon>
              <TimelineEventCopy>
                <TimelineEventDuration>
                  Feb 2021 - Apr 2021
                </TimelineEventDuration>
                <h3>Frontend Engineer Consultant</h3>
                <h4>Stealth Startup</h4>
                <p>
                Worked on a CSP security scanner software using NextJS, TypeScript, Node and Tailwind. 
                <br />
                <br />
                Delivered successful features, debugged and polished existing dashboard UI.
                </p>
              </TimelineEventCopy>
            </TimelineEvent>
            <TimelineEvent>
              <TimelineEventIcon></TimelineEventIcon>
              <TimelineEventCopy>
                <TimelineEventDuration>
                  Jun 2020 - Aug 2020
                </TimelineEventDuration>
                <h3>Software Engineer Fellow</h3>
                <h4>Major League Hacking</h4>
                <p>
                  Built a scheduling profiling tool for the React Devtools,
                  using JavaScript and Canvas, to visualize and improve
                  application performance in React's cooperative scheduling
                  mode, called Concurrent Mode.
                </p>
              </TimelineEventCopy>
            </TimelineEvent>
            <TimelineEvent>
              <TimelineEventIcon></TimelineEventIcon>
              <TimelineEventCopy>
                <TimelineEventDuration>2018 - 2019</TimelineEventDuration>
                <h3>Web Developer</h3>
                <h4>Freelance/Volunteer</h4>
                <p>
                  Built visually stunning, user friendly websites through
                  volunteership and freelance activities.
                  <br />
                  <br />
                  Used HTML and CSS for translating desired visual designs to
                  code and JavaScript for adding interactivity to the
                  components.
                </p>
              </TimelineEventCopy>
            </TimelineEvent>
          </Timeline>

          <Heading>
            If you like my work<span>,</span>
          </Heading>
          <Description>
            <p>
              <i>
                I'm a Software Engineer looking for an impactful role in a
                dynamic<span>,</span> mission-driven product company
                <span>.</span>
              </i>
              <br />
              <br />I care about the impact I can have in your company
              <span>,</span> and the positive impact we’ll have together for the
              benefit of our users<span>.</span>
              <br />
              <br />
              If you've got an interesting project that could benefit from my
              skillset
              <span>,</span> I would love to get along and work on your product
              <span>.</span>
              {/* I am proficient in web application design and engineering
            using React, GraphQL, TypeScript/Flow, web standards, and various
            other frameworks, libraries and tools. */}
            </p>
            <br></br>
          </Description>
          <Description long={true}>
            <hr></hr>
            <br></br>
            <h2>
              Reach out to me<span>!</span>
            </h2>
            <p>
              Drop a message<span>,</span> my inbox is always open
              <span>!</span>
              <br />
              <span>(</span> unless the internet goes down<span>,</span> then
              just throw a pigeon my way <span>:)</span>
            </p>
            <Social />
          </Description>
        </Content>
        <Footer>
          Designed by <a href="https://www.linkedin.com/in/kartikcho/">me !</a>
        </Footer>
      </Page>
      <GlobalStyles />
    </>
  );
};

export default Index;

export const overviewQuery = graphql`
  query OverviewQuery {
    allSitesYaml {
      edges {
        node {
          id
          title
          url
          name
          description
          preview
          features
          year
          cover {
            childImageSharp {
              fluid(maxWidth: 800) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
    }
    site {
      siteMetadata {
        siteTitle
        siteUrl
        siteDescription
      }
    }
  }
`;
